/**
 * @ims/shared — Config Loader
 *
 * Fetch-based loader for config JSON files with optional schema validation.
 * Throws typed errors for network, parse, and validation failures.
 *
 * @see docs/ims-brand-frontends/02-config-system.md — Config Loader
 */

import { validateConfig, type ConfigSchemaKey } from '../validation/validateConfig';

// ---------------------------------------------------------------------------
// Typed Config Errors
// ---------------------------------------------------------------------------

export type ConfigErrorCode =
  | 'CONFIG_NOT_FOUND'
  | 'CONFIG_PARSE_ERROR'
  | 'CONFIG_VALIDATION_ERROR'
  | 'CONFIG_NETWORK_ERROR';

/** Structured error thrown by the config loader. */
export class ConfigError extends Error {
  readonly code: ConfigErrorCode;
  readonly url: string;
  readonly validationErrors?: string[];

  constructor(code: ConfigErrorCode, message: string, url: string, validationErrors?: string[]) {
    super(message);
    this.name = 'ConfigError';
    this.code = code;
    this.url = url;
    this.validationErrors = validationErrors;
  }
}

// ---------------------------------------------------------------------------
// loadConfig
// ---------------------------------------------------------------------------

/**
 * Fetch a JSON config from `url` and optionally validate it against a schema.
 *
 * @throws {ConfigError} CONFIG_NOT_FOUND   — HTTP 404
 * @throws {ConfigError} CONFIG_NETWORK_ERROR — fetch failed (offline, CORS, etc.)
 * @throws {ConfigError} CONFIG_PARSE_ERROR — response is not valid JSON
 * @throws {ConfigError} CONFIG_VALIDATION_ERROR — JSON does not match schema
 *
 * @example
 * const brand = await loadConfig<BrandConfig>('/content/brands/audi/brand.json', 'brand');
 */
export async function loadConfig<T>(url: string, schemaKey?: ConfigSchemaKey): Promise<T> {
  // 1. Network request
  let response: Response;
  try {
    response = await fetch(url);
  } catch (cause) {
    throw new ConfigError(
      'CONFIG_NETWORK_ERROR',
      `Network request failed for "${url}": ${cause instanceof Error ? cause.message : String(cause)}`,
      url,
    );
  }

  // 2. 404 check
  if (response.status === 404) {
    throw new ConfigError('CONFIG_NOT_FOUND', `Config not found: "${url}" (HTTP 404)`, url);
  }

  if (!response.ok) {
    throw new ConfigError(
      'CONFIG_NETWORK_ERROR',
      `Failed to load config "${url}" (HTTP ${response.status})`,
      url,
    );
  }

  // 2b. SPA-fallback guard.
  //     Both the Vite dev middleware and the Firebase hosting rewrite answer
  //     unknown paths with `index.html` and HTTP 200. Without this check a market
  //     that simply does not exist is indistinguishable from a corrupt config:
  //     the HTML would reach JSON.parse and surface as CONFIG_PARSE_ERROR.
  const contentType = response.headers?.get('content-type') ?? '';
  if (contentType && !/\bjson\b/i.test(contentType)) {
    throw new ConfigError(
      'CONFIG_NOT_FOUND',
      `Config not found: "${url}" — server returned "${contentType}" instead of JSON ` +
        `(SPA fallback). The file most likely does not exist.`,
      url,
    );
  }

  // 3. JSON parse
  let data: unknown;
  try {
    data = (await response.json()) as unknown;
  } catch {
    throw new ConfigError('CONFIG_PARSE_ERROR', `Invalid JSON in config "${url}"`, url);
  }

  // 4. Optional schema validation
  if (schemaKey) {
    const result = validateConfig<T>(schemaKey, data);
    if (!result.valid) {
      throw new ConfigError(
        'CONFIG_VALIDATION_ERROR',
        `Config "${url}" failed schema validation (${schemaKey})`,
        url,
        result.errors,
      );
    }
    return result.data;
  }

  return data as T;
}
